/**
 * Hook Manager
 * Orchestrates hook execution for Metis Code
 * Inspired by Claude Code's hook system
 */

import { spawn, execSync } from 'child_process';
import fs from 'fs';
import path from 'path';
import os from 'os';
import { EventEmitter } from 'events';
import {
  HookDefinition,
  HookType,
  HookInput,
  HookResult,
  HooksConfig,
  HookContext,
  PreToolUseInput,
  PostToolUseInput,
  PermissionRequestInput,
} from './types';

const DEFAULT_TIMEOUT = 30000; // 30 seconds
const MAX_CONCURRENT_HOOKS = 5;

export class HookManager extends EventEmitter {
  private hooks: HookDefinition[] = [];
  private config: HooksConfig;
  private context: HookContext;
  private runningHooks: Map<string, AbortController> = new Map();

  constructor(projectRoot: string, sessionId: string) {
    super();

    this.context = {
      cwd: projectRoot,
      sessionId,
      env: process.env as Record<string, string>,
      homeDir: os.homedir(),
      projectRoot,
    };

    this.config = {
      enabled: true,
      hooks: [],
      defaultTimeout: DEFAULT_TIMEOUT,
      maxConcurrent: MAX_CONCURRENT_HOOKS,
      continueOnError: true,
    };

    this.loadHooks();
  }

  /**
   * Load hooks from configuration files
   * Priority: project .metis/hooks.json > global ~/.metis/hooks.json
   */
  private loadHooks(): void {
    const hooksPaths = [
      path.join(this.context.homeDir, '.metis', 'hooks.json'),
      path.join(this.context.projectRoot, '.metis', 'hooks.json'),
    ];

    for (const hooksPath of hooksPaths) {
      if (fs.existsSync(hooksPath)) {
        try {
          const content = fs.readFileSync(hooksPath, 'utf8');
          const config = JSON.parse(content) as Partial<HooksConfig>;

          if (config.hooks) {
            // Merge hooks, project hooks override global
            this.hooks = [...this.hooks, ...config.hooks];
          }

          if (config.enabled !== undefined) {
            this.config.enabled = config.enabled;
          }

          if (config.defaultTimeout) {
            this.config.defaultTimeout = config.defaultTimeout;
          }

          if (config.continueOnError !== undefined) {
            this.config.continueOnError = config.continueOnError;
          }
        } catch (error) {
          console.warn(`Failed to load hooks from ${hooksPath}:`, error);
        }
      }
    }

    // Assign IDs to hooks that don't have them
    this.hooks = this.hooks.map((hook, index) => ({
      ...hook,
      id: hook.id || `hook-${index}`,
      enabled: hook.enabled !== false,
    }));
  }

  /**
   * Reload hooks from configuration
   */
  reload(): void {
    this.hooks = [];
    this.loadHooks();
  }

  /**
   * Register a hook programmatically
   */
  registerHook(hook: HookDefinition): string {
    const id = hook.id || `hook-${Date.now()}`;
    this.hooks.push({ ...hook, id, enabled: hook.enabled !== false });
    return id;
  }

  /**
   * Unregister a hook by ID
   */
  unregisterHook(id: string): boolean {
    const index = this.hooks.findIndex(h => h.id === id);
    if (index !== -1) {
      this.hooks.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Get all registered hooks
   */
  getHooks(): HookDefinition[] {
    return [...this.hooks];
  }

  /**
   * Get hooks by type
   */
  getHooksByType(type: HookType): HookDefinition[] {
    return this.hooks.filter(h => h.type === type && h.enabled);
  }

  /**
   * Check if hooks are enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Enable/disable hooks globally
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Execute pre-tool-use hooks
   */
  async executePreToolHooks(input: PreToolUseInput): Promise<HookResult[]> {
    if (!this.config.enabled) return [];

    const matchingHooks = this.hooks.filter(
      h => h.type === 'PreToolUse' && h.enabled && this.matchesTool(h, input.tool_name)
    );

    return this.executeHooks(matchingHooks, input);
  }

  /**
   * Execute post-tool-use hooks
   */
  async executePostToolHooks(input: PostToolUseInput): Promise<HookResult[]> {
    if (!this.config.enabled) return [];

    const matchingHooks = this.hooks.filter(
      h => h.type === 'PostToolUse' && h.enabled && this.matchesTool(h, input.tool_name)
    );

    return this.executeHooks(matchingHooks, input);
  }

  /**
   * Execute permission request hooks
   * Returns decision if any hook provides one
   */
  async executePermissionHooks(input: PermissionRequestInput): Promise<{
    results: HookResult[];
    decision?: 'allow' | 'deny' | 'ask';
  }> {
    if (!this.config.enabled) return { results: [] };

    const matchingHooks = this.hooks.filter(
      h => h.type === 'PermissionRequest' && h.enabled && this.matchesTool(h, input.tool_name)
    );

    const results = await this.executeHooks(matchingHooks, input);

    // Check if any hook made a decision
    for (const result of results) {
      if (result.decision) {
        return { results, decision: result.decision };
      }
    }

    return { results };
  }

  /**
   * Execute hooks of a specific type
   */
  async executeHooksByType(type: HookType, input: HookInput): Promise<HookResult[]> {
    if (!this.config.enabled) return [];

    const matchingHooks = this.hooks.filter(h => h.type === type && h.enabled);
    return this.executeHooks(matchingHooks, input);
  }

  /**
   * Execute a list of hooks
   */
  private async executeHooks(hooks: HookDefinition[], input: HookInput): Promise<HookResult[]> {
    const results: HookResult[] = [];

    // Separate blocking and non-blocking hooks
    const blockingHooks = hooks.filter(h => h.blocking !== false);
    const nonBlockingHooks = hooks.filter(h => h.blocking === false);

    // Execute blocking hooks sequentially
    for (const hook of blockingHooks) {
      try {
        const result = await this.executeHook(hook, input);
        results.push(result);

        // If hook blocks, stop execution
        if (result.block) {
          this.emit('hook:blocked', { hook, result });
          break;
        }
      } catch (error) {
        const errorResult: HookResult = {
          success: false,
          error: error instanceof Error ? error.message : String(error),
          duration_ms: 0,
        };
        results.push(errorResult);
        this.emit('hook:error', { hook, error });

        if (!this.config.continueOnError) {
          break;
        }
      }
    }

    // Execute non-blocking hooks in parallel (fire and forget)
    for (const hook of nonBlockingHooks) {
      this.executeHook(hook, input).catch(error => {
        this.emit('hook:error', { hook, error });
      });
    }

    return results;
  }

  /**
   * Execute a single hook
   */
  private async executeHook(hook: HookDefinition, input: HookInput): Promise<HookResult> {
    const startTime = Date.now();
    const timeout = hook.timeout || this.config.defaultTimeout || DEFAULT_TIMEOUT;

    this.emit('hook:start', { hook, input });

    try {
      if (hook.command) {
        // Shell command hook
        const result = await this.executeCommand(hook, input, timeout);
        this.emit('hook:complete', { hook, result });
        return result;
      } else if (hook.prompt) {
        // Prompt-based hook (for AI evaluation)
        const result = await this.executePromptHook(hook, input, timeout);
        this.emit('hook:complete', { hook, result });
        return result;
      } else {
        return {
          success: false,
          error: 'Hook has no command or prompt defined',
          duration_ms: Date.now() - startTime,
        };
      }
    } catch (error) {
      const result: HookResult = {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        duration_ms: Date.now() - startTime,
      };
      this.emit('hook:error', { hook, error });
      return result;
    }
  }

  /**
   * Execute a shell command hook
   */
  private executeCommand(
    hook: HookDefinition,
    input: HookInput,
    timeout: number
  ): Promise<HookResult> {
    return new Promise((resolve) => {
      const startTime = Date.now();

      // Interpolate variables in command
      const command = this.interpolateCommand(hook.command!, input);
      const cwd = hook.cwd || this.context.projectRoot;

      // Build environment
      const env = {
        ...process.env,
        ...hook.env,
        METIS_SESSION_ID: this.context.sessionId,
        METIS_PROJECT_ROOT: this.context.projectRoot,
        METIS_HOOK_TYPE: hook.type,
        METIS_HOOK_INPUT: JSON.stringify(input),
      };

      // Create abort controller for timeout
      const abortController = new AbortController();
      const hookId = hook.id || `hook-${Date.now()}`;
      this.runningHooks.set(hookId, abortController);

      let stdout = '';
      let stderr = '';

      const child = spawn('sh', ['-c', command], {
        cwd,
        env,
        signal: abortController.signal,
      });

      const timeoutId = setTimeout(() => {
        abortController.abort();
        this.emit('hook:timeout', { hook });
      }, timeout);

      child.stdout?.on('data', (data) => {
        stdout += data.toString();
      });

      child.stderr?.on('data', (data) => {
        stderr += data.toString();
      });

      child.on('close', (code) => {
        clearTimeout(timeoutId);
        this.runningHooks.delete(hookId);

        const duration_ms = Date.now() - startTime;
        const output = stdout.trim();

        // Parse special directives from output
        const result = this.parseHookOutput(output, code || 0, duration_ms);

        if (code !== 0 && !result.block) {
          result.error = stderr.trim() || `Command exited with code ${code}`;
        }

        resolve(result);
      });

      child.on('error', (error) => {
        clearTimeout(timeoutId);
        this.runningHooks.delete(hookId);

        resolve({
          success: false,
          error: error.message,
          duration_ms: Date.now() - startTime,
        });
      });
    });
  }

  /**
   * Execute a prompt-based hook (placeholder for AI evaluation)
   */
  private async executePromptHook(
    hook: HookDefinition,
    input: HookInput,
    timeout: number
  ): Promise<HookResult> {
    // This would integrate with the AI provider for evaluation
    // For now, return a placeholder
    return {
      success: true,
      output: 'Prompt hooks not yet implemented',
      duration_ms: 0,
    };
  }

  /**
   * Interpolate variables in command string
   */
  private interpolateCommand(command: string, input: HookInput): string {
    let result = command;

    // Replace ${var} patterns with input values
    const inputRecord = input as Record<string, any>;

    for (const [key, value] of Object.entries(inputRecord)) {
      const placeholder = `\${${key}}`;
      if (result.includes(placeholder)) {
        const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
        result = result.replace(new RegExp(`\\$\\{${key}\\}`, 'g'), stringValue);
      }
    }

    // Also support $var syntax
    for (const [key, value] of Object.entries(inputRecord)) {
      const placeholder = `$${key}`;
      if (result.includes(placeholder)) {
        const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
        result = result.replace(new RegExp(`\\$${key}\\b`, 'g'), stringValue);
      }
    }

    // Special variable for file path in tool_input
    if ('tool_input' in input && input.tool_input?.file_path) {
      result = result.replace(/\$\{file\}/g, input.tool_input.file_path);
      result = result.replace(/\$file\b/g, input.tool_input.file_path);
    }

    return result;
  }

  /**
   * Parse hook output for special directives
   */
  private parseHookOutput(output: string, exitCode: number, duration_ms: number): HookResult {
    const result: HookResult = {
      success: exitCode === 0,
      output,
      exitCode,
      duration_ms,
    };

    // Check for special directives in output
    const lines = output.split('\n');

    for (const line of lines) {
      // METIS_BLOCK: message - blocks the operation
      if (line.startsWith('METIS_BLOCK:')) {
        result.block = true;
        result.blockMessage = line.substring('METIS_BLOCK:'.length).trim();
      }

      // METIS_ALLOW - auto-allow permission
      if (line.trim() === 'METIS_ALLOW') {
        result.decision = 'allow';
      }

      // METIS_DENY - auto-deny permission
      if (line.trim() === 'METIS_DENY') {
        result.decision = 'deny';
      }

      // METIS_ASK - ask user (default)
      if (line.trim() === 'METIS_ASK') {
        result.decision = 'ask';
      }
    }

    return result;
  }

  /**
   * Check if a hook matches a tool name
   */
  private matchesTool(hook: HookDefinition, toolName: string): boolean {
    if (!hook.matcher) return true; // No matcher = matches all

    if (hook.matcher instanceof RegExp) {
      return hook.matcher.test(toolName);
    }

    // String matcher - support wildcards
    const pattern = hook.matcher
      .replace(/\*/g, '.*')
      .replace(/\?/g, '.');

    const regex = new RegExp(`^${pattern}$`);
    return regex.test(toolName);
  }

  /**
   * Cancel all running hooks
   */
  cancelAll(): void {
    for (const [id, controller] of this.runningHooks) {
      controller.abort();
    }
    this.runningHooks.clear();
  }

  /**
   * Get running hooks count
   */
  getRunningCount(): number {
    return this.runningHooks.size;
  }

  /**
   * Save hooks configuration to file
   */
  saveConfig(global: boolean = false): void {
    const configPath = global
      ? path.join(this.context.homeDir, '.metis', 'hooks.json')
      : path.join(this.context.projectRoot, '.metis', 'hooks.json');

    const dir = path.dirname(configPath);
    if (!fs.existsSync(dir)) {
      fs.mkdirSync(dir, { recursive: true });
    }

    const config: HooksConfig = {
      enabled: this.config.enabled,
      hooks: this.hooks,
      defaultTimeout: this.config.defaultTimeout,
      continueOnError: this.config.continueOnError,
    };

    fs.writeFileSync(configPath, JSON.stringify(config, null, 2));
  }
}

// Global hook manager instance
let globalHookManager: HookManager | null = null;

export function getHookManager(projectRoot?: string, sessionId?: string): HookManager {
  if (!globalHookManager) {
    globalHookManager = new HookManager(
      projectRoot || process.cwd(),
      sessionId || `session-${Date.now()}`
    );
  }
  return globalHookManager;
}

export function resetHookManager(): void {
  if (globalHookManager) {
    globalHookManager.cancelAll();
    globalHookManager = null;
  }
}
